// src/models/authModel.js - Apartment Management System Frontend
import api from '../services/api';


// Get auth token from localStorage
export const getAuthToken = () => {
    return localStorage.getItem('token');
};

// Check if user is authenticated
export const isAuthenticated = () => {
    const token = getAuthToken();
    const user = getCurrentUser();
    return !!(token && user);
};

// Get current user from localStorage
export const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    try {
        return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
    }
};

// Login user
export const loginUser = async (username, password) => {
    try {
        const response = await api.post('/auth/login', {
            username,
            password
        });

        // Only persist token/user when OTP is NOT required (full login)
        if (response.data.success && response.data.token && !response.data.requireOtp) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Login failed' };
    }
};

// Verify OTP
export const verifyOTP = async (userId, otp) => {
    try {
        const response = await api.post('/auth/verify-otp', {
            userId,
            otp
        });

        if (response.data.success && response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Verification failed' };
    }
};

// Resend OTP
export const resendOTP = async (userId, email) => {
    try {
        const response = await api.post('/auth/resend-otp', {
            userId,
            email
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Resend failed' };
    }
};

// Register user
export const registerUser = async (userData) => {
    try {
        const response = await api.post('/auth/register', userData);

        if (response.data.success && response.data.token) {
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Registration failed' };
    }
};

// Logout user
export const logoutUser = async () => {
    try {
        const token = getAuthToken();
        if (token) {
            await api.post('/users/logout', {});
        }
    } catch (error) {
        console.error('Logout error:', error);
    } finally {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
};

// Check if token is expired
export const isTokenExpired = () => {
    const token = getAuthToken();
    if (!token) return true;

    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        return payload.exp < currentTime;
    } catch (error) {
        console.error('Error checking token expiration:', error);
        return true;
    }
};

// Auto logout if token is expired
export const checkTokenExpiration = () => {
    if (isAuthenticated() && isTokenExpired()) {
        logoutUser();
        window.location.href = '/login';
    }
};

// Set up automatic token expiration check
setInterval(checkTokenExpiration, 60000); // Check every minute

export default {
    getAuthToken,
    isAuthenticated,
    getCurrentUser,
    loginUser,
    verifyOTP,
    resendOTP,
    registerUser,
    logoutUser,
    isTokenExpired,
    checkTokenExpiration
};